import { aggregateBigIntProps } from '@silo/utils';
import {
  CalculateLTVParams,
  CollateralAsset,
  CollateralAssetMetrics,
  CollateralAssetWithValues,
  DebtAsset,
  DebtAssetMetrics,
  DebtAssetWithValues,
  BaseLTV,
} from './types';
import { LiquidationPriceLegacy, PRECISION_DECIMALS, PRECISION_FACTOR } from '@silo/types';

export const performCalculations = (args: CalculateLTVParams): BaseLTV => {
  const collateralAssetsWithValues: CollateralAssetWithValues[] =
    args.collateralAssets.map(mapToCollateralAssetWithValues);
  const debtAssetsWithValues: DebtAssetWithValues[] = args.debtAssets.map(mapToDebtAssetWithValues);

  const { availableToBorrowValue, liquidationThresholdValue, collateralValue } = aggregateBigIntProps(
    collateralAssetsWithValues,
    ['availableToBorrowValue', 'liquidationThresholdValue', 'collateralValue']
  );

  const { borrowedValue } = aggregateBigIntProps(debtAssetsWithValues, ['borrowedValue']);

  const remainingToBorrowValue = availableToBorrowValue - borrowedValue;
  const liquidationBufferValue = liquidationThresholdValue - borrowedValue;
  const collateralAssets = collateralAssetsWithValues.map((asset) =>
    mapToCollateralAssetMetrics({ asset, remainingToBorrowValue, liquidationBufferValue })
  );
  const debtAssets = debtAssetsWithValues.map((asset) =>
    mapToDebtAssetMetrics({ asset, remainingToBorrowValue, liquidationBufferValue })
  );

  if (collateralValue === 0n) {
    return getNoCollateralLTV({ borrowedValue, collateralAssets, debtAssets });
  }

  const userLTV = (borrowedValue * PRECISION_FACTOR) / collateralValue;
  const userMaximumLTV = (availableToBorrowValue * PRECISION_FACTOR) / collateralValue;
  const userLiquidationThreshold = (liquidationThresholdValue * PRECISION_FACTOR) / collateralValue;
  const riskFactor = (borrowedValue * PRECISION_FACTOR) / liquidationThresholdValue;
  const healthFactor = PRECISION_FACTOR - riskFactor;
  const borrowedPct = (borrowedValue * PRECISION_FACTOR) / availableToBorrowValue;
  const remainingToBorrowPct = (remainingToBorrowValue * PRECISION_FACTOR) / availableToBorrowValue;

  return {
    userLTV,
    userMaximumLTV,
    userLiquidationThreshold,
    healthFactor,
    riskFactor,
    collateralValue,
    liquidationThresholdValue,
    availableToBorrowValue,
    borrowedValue,
    remainingToBorrowValue,
    liquidationBufferValue,
    borrowedPct,
    remainingToBorrowPct,
    collateralAssets,
    debtAssets,
  };
};

type GetNoCollateralLTV = (params: {
  borrowedValue: bigint;
  collateralAssets: CollateralAssetMetrics[];
  debtAssets: DebtAssetMetrics[];
}) => BaseLTV;

const getNoCollateralLTV: GetNoCollateralLTV = ({ borrowedValue, collateralAssets, debtAssets }) => ({
  userLTV: 0n,
  userMaximumLTV: 0n,
  userLiquidationThreshold: 0n,
  healthFactor: 0n,
  riskFactor: 10n ** BigInt(PRECISION_DECIMALS),
  collateralValue: 0n,
  liquidationThresholdValue: 0n,
  availableToBorrowValue: 0n,
  borrowedValue,
  remainingToBorrowValue: 0n,
  liquidationBufferValue: 0n,
  borrowedPct: 0n,
  remainingToBorrowPct: 0n,
  collateralAssets,
  debtAssets,
});

export const mapToCollateralAssetWithValues = (asset: CollateralAsset): CollateralAssetWithValues => {
  const { balance, price, maximumLTV, liquidationThreshold } = asset;
  const collateralValue = (balance * price) / PRECISION_FACTOR;

  return {
    ...asset,
    collateralValue,
    availableToBorrowValue: (maximumLTV * collateralValue) / PRECISION_FACTOR,
    liquidationThresholdValue: (liquidationThreshold * collateralValue) / PRECISION_FACTOR,
  };
};

export const mapToDebtAssetWithValues = (asset: DebtAsset): DebtAssetWithValues => {
  const { balance, price } = asset;

  return {
    ...asset,
    borrowedValue: (balance * price) / PRECISION_FACTOR,
  };
};

type MapToCollateralAssetMetrics = (params: {
  asset: CollateralAssetWithValues;
  remainingToBorrowValue: bigint;
  liquidationBufferValue: bigint;
}) => CollateralAssetMetrics;

const mapToCollateralAssetMetrics: MapToCollateralAssetMetrics = ({
  asset,
  remainingToBorrowValue,
  liquidationBufferValue,
}) => {
  const { type, borrowValueToReduce } =
    asset.availableToBorrowValue < remainingToBorrowValue
      ? ({ type: 'protocolBalance', borrowValueToReduce: asset.availableToBorrowValue } as const)
      : ({ type: 'ltvLimit', borrowValueToReduce: remainingToBorrowValue } as const);

  const { assetAddress, decimals } = asset;

  /**
   * When the asset price is unavailable (oracle removed/reverting → price 0), it
   * contributes no borrowing power, so the user can withdraw their full deposited
   * balance. Guard here to avoid a bigint division-by-zero, and skip the (now
   * meaningless) liquidation price.
   */
  if (asset.price === 0n) {
    return {
      assetAddress,
      decimals,
      maxWithdrawTokens: { type: 'protocolBalance', value: asset.balance },
      liquidationPrice: { _tag: 'notApplicable' },
    };
  }

  const maxWithdrawValue = (borrowValueToReduce * PRECISION_FACTOR) / asset.maximumLTV;
  const maxWithdrawTokens = (maxWithdrawValue * PRECISION_FACTOR) / asset.price;

  const getLiquidationPrice = (): LiquidationPriceLegacy => {
    if (asset.liquidationThresholdValue <= liquidationBufferValue) {
      return { _tag: 'notApplicable' };
    }

    if (asset.balance === 0n) {
      return { _tag: 'notApplicable' };
    }

    const minLiquidationThresholdValue = asset.liquidationThresholdValue - liquidationBufferValue;
    const minCollateralValue = (minLiquidationThresholdValue * PRECISION_FACTOR) / asset.liquidationThreshold;
    const liquidationPrice = (minCollateralValue * PRECISION_FACTOR) / asset.balance;

    return { _tag: 'applicable', value: liquidationPrice };
  };

  return {
    assetAddress,
    decimals,
    maxWithdrawTokens: { type, value: maxWithdrawTokens },
    liquidationPrice: getLiquidationPrice(),
  };
};

type MapToDebtAssetMetrics = (params: {
  asset: DebtAssetWithValues;
  remainingToBorrowValue: bigint;
  liquidationBufferValue: bigint;
}) => DebtAssetMetrics;

const mapToDebtAssetMetrics: MapToDebtAssetMetrics = ({ asset, remainingToBorrowValue, liquidationBufferValue }) => {
  const borrowedTokens = asset.balance;
  // Guard against a 0 price (unavailable oracle): can't borrow more of an unpriced asset.
  const remainingToBorrowTokens =
    asset.price === 0n ? 0n : (remainingToBorrowValue * PRECISION_FACTOR) / asset.price;
  const availableToBorrowTokens = borrowedTokens + remainingToBorrowTokens;
  const { assetAddress, decimals } = asset;

  const getLiquidationPrice = (): LiquidationPriceLegacy => {
    if (asset.balance === 0n) {
      return { _tag: 'notApplicable' };
    }

    const maxValue = liquidationBufferValue + asset.borrowedValue;
    const liquidationPrice = (maxValue * PRECISION_FACTOR) / asset.balance;

    return { _tag: 'applicable', value: liquidationPrice };
  };

  return {
    assetAddress,
    decimals,
    borrowedTokens,
    remainingToBorrowTokens,
    availableToBorrowTokens,
    liquidationPrice: getLiquidationPrice(),
  };
};
